import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuthContext } from "../contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface ProfileCompletionGuardProps {
  children: React.ReactNode;
}

export default function ProfileCompletionGuard({ children }: ProfileCompletionGuardProps) {
  const { user, loading: authLoading } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (authLoading) return;

      if (!user) {
        setChecking(false);
        return;
      }

      if (user.role !== "student") {
        setChecking(false);
        return;
      }

      if (location.pathname === "/complete-profile") {
        setChecking(false);
        return;
      }

      try {
        const profileRef = doc(db, "studentProfiles", user.uid);
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists() || !profileSnap.data()?.completed) {
          navigate("/complete-profile", { replace: true });
          return;
        }

        setChecking(false);
      } catch (error) {
        console.error("Error checking profile completion:", error);
        setChecking(false);
      }
    };

    checkProfileCompletion();
  }, [user, authLoading, navigate, location.pathname]);

  if (authLoading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return <>{children}</>;
}
